import _ from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { FilterMatchMode } from 'primereact/api'

// Components
import { Button } from '../../components/Button'
import { ClientHeader } from '../../components/ClientHeader'
import { CustomLink } from '../../components/CustomLink'
import {
  CustomTextFilter,
  DateEditor,
  PercentageEditor,
  PriceEditor,
  StatusEditor,
  TextAreaEditor,
  TextEditor,
  renderMultiSelectFilter,
  renderRadioFilter,
  renderSelectEditor,
  renderStatusTag,
} from '../../components/CustomEditor'
import {
  DataTable,
  CUSTOM_FILTER_OPTIONS,
  DEFAULT_FILTER_OPTIONS,
  DEFAULT_NUMBER_OPTIONS,
} from '../../components/DataTable'

import { PageContainer } from '../../components/PageContainer'
import { NoDataPrompt } from '../../components/NoDataPrompt'
import { StateContainer } from '../../components/StateContainer'
import { Toggle } from '../../components/Toggle'
import { Tooltip } from '../../components/Tooltip'

// Services
import {
  getClient,
  getClientPortfolioDataAccounts,
  getClientPortfolioDataComposites,
  getClientPortfolioDataPeriods,
  getClientPortfolioDataPortfolios,
} from '../../services/clients.service'
import {
  deletePortfolioData,
  getPortfolioData,
  updatePortfolioData,
} from '../../services/portfolio.service'

// Store
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import {
  configureFiltersFromSearchParams,
  configureFilterQuery,
  hasAnyFilterSelected,
  toast,
} from '../../utils/helpers'
import { formatCurrency } from '../../utils/formatters'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

// Local
import AddPortfolioDataModal from './AddPortfolioDataModal'
import ExportDataModal from './ExportDataModal'

const RADIO_OPTIONS = [
  { label: 'Has Violations', value: true },
  { label: 'No Violations', value: false },
  { label: 'All', value: null },
]

const YES_OR_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
  { label: 'All', value: null },
]

const DEFAULT_FILTERS = {
  account: { value: [] },
  composite: { value: [] },
  period: { value: [] },
  portfolio: { value: [] },
  beginningValue: { value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  endingValue: { value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  grossReturn: { value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  netReturn: { value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  isIncluded: { value: null },
  isFeePaying: { value: null },
  hasBundledFee: { value: null },
  comments: { value: null, matchMode: FilterMatchMode.CONTAINS },
  ruleViolations: { value: null },
}

const Portfolio = observer(() => {
  // Context
  const { clientId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { canModifyData, isImportInProgress, client, setClient } = useContext(
    ClientDashboardStoreContext,
  )

  // Pagination
  const { pagination, setTotalRecords } = usePagination(50)
  const { perPage, currentPage } = pagination

  // Sorting
  const { sorting } = useSorting('period')
  const { sortedColumn } = sorting

  // State
  const [error, setError] = useState(null)
  const [loadingPortfolioData, setLoadingPortfolioData] = useState(true)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingClient, setLoadingClient] = useState(true)
  const [showAddRowModal, setShowAddRowModal] = useState(false)
  const [showExportDataModal, setShowExportDataModal] = useState(false)
  const [columns, setColumns] = useState([])
  const [portfolioData, setPortfolioData] = useState([])
  const [dataEditable, setDataEditable] = useState(false)
  const [filters, setFilters] = useState(
    configureFiltersFromSearchParams(searchParams, DEFAULT_FILTERS),
  )
  const [accounts, setAccounts] = useState([])
  const [composites, setComposites] = useState([])
  const [periods, setPeriods] = useState([])
  const [portfolios, setPortfolios] = useState([])
  const [refreshCounter, setRefreshCounter] = useState(0)

  const refreshPortfolioData = () => setRefreshCounter((c) => c + 1)

  const dataTable = useRef()

  const PORTFOLIO_DATA_BASE_URL = `/clients/${clientId}/portfolio-data?expand=composite,account,portfolio`

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  const getUpdatedClient = async (initialLoad = false) => {
    const updatedClient = await getClient(
      clientId,
      setError,
      initialLoad ? setLoadingClient : () => {},
    )

    if (updatedClient) {
      setClient(updatedClient)
    }

    return updatedClient
  }

  useEffect(() => {
    const getClientData = async () => {
      const updatedClient = await getUpdatedClient(true)

      const [clientAccounts, clientComposites, clientPeriods, clientPortfolios] =
        await Promise.all([
          getClientPortfolioDataAccounts(clientId, handleErrors),
          getClientPortfolioDataComposites(clientId, handleErrors),
          getClientPortfolioDataPeriods(clientId, handleErrors),
          updatedClient?.hasAnyHouseholdData &&
            getClientPortfolioDataPortfolios(clientId, handleErrors),
        ])

      const updatedAccounts = _.map(clientAccounts, (d) => ({
        ...d,
        label: `${d.name} (${d.number})`,
        value: d.id,
        id: d.id,
      }))

      const updatedComposites = _.map(clientComposites, (d) => ({
        ...d,
        label: d.name,
        value: d.id,
        id: d.id,
      }))

      const updatedPeriods = _.map(clientPeriods, (d) => ({
        label: dayjs(d).format('MM/DD/YYYY'),
        value: d,
        id: d,
      }))

      let updatedPortfolios = []
      if (updatedClient?.hasAnyHouseholdData && clientPortfolios) {
        updatedPortfolios = _.map(clientPortfolios, (d) => ({
          ...d,
          label: `${d.name} (${d.number})`,
          value: d.id,
          id: d.id,
        }))
      }

      const DEFAULT_COLUMNS = [
        {
          field: 'composite.id',
          header: 'Composite',
          body: (row) => row.composite.name,
          sortField: 'composite__name',
          sortable: true,
          style: { minWidth: '250px' },
          filterField: 'composite',
          ...CUSTOM_FILTER_OPTIONS,
          editor: (options) =>
            renderSelectEditor(
              updatedComposites,
              options.rowData.composite.id,
              'Select a Composite',
              options,
            ),
          filterElement: (options) =>
            renderMultiSelectFilter(
              updatedComposites,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  composite: {
                    value: selected,
                  },
                }))
              },
              'Filter by Composite',
              options,
            ),
        },
        {
          field: 'account.id',
          header: 'Account',
          body: (row) => `${row.account.name} (${row.account.number})`,
          sortField: 'account__name',
          sortable: true,
          style: { minWidth: '230px' },
          filterField: 'account',
          ...CUSTOM_FILTER_OPTIONS,
          editor: (options) =>
            renderSelectEditor(
              updatedAccounts,
              options.rowData.account.id,
              'Select an Account',
              options,
            ),
          filterElement: (options) =>
            renderMultiSelectFilter(
              updatedAccounts,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  account: {
                    value: selected,
                  },
                }))
              },
              'Filter by Account',
              options,
            ),
        },
        {
          field: 'period',
          header: 'Period',
          sortable: true,
          body: (row) => dayjs(row.period).format('MM/DD/YYYY'),
          editor: DateEditor,
          style: { minWidth: '150px' },
          filterField: 'period',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderMultiSelectFilter(
              updatedPeriods,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  period: {
                    value: selected,
                  },
                }))
              },
              'Filter by Period',
              options,
            ),
        },
        {
          field: 'beginningValue',
          header: 'Beginning Value',
          sortable: true,
          editor: PriceEditor,
          body: (row) => formatCurrency(row.beginningValue),
          style: { minWidth: '200px' },
          filterField: 'beginningValue',
          ...DEFAULT_NUMBER_OPTIONS,
          filterElement: (options) => (
            <CustomTextFilter
              options={options}
              handleChange={(value) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  beginningValue: {
                    value,
                    matchMode: prevFilters.beginningValue.matchMode,
                  },
                }))
              }}
              type="Number"
            />
          ),
          onFilterMatchModeChange: (_field, mode) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              beginningValue: {
                value: prevFilters.beginningValue.value,
                matchMode: mode,
              },
            }))
          },
        },
        {
          field: 'endingValue',
          header: 'Ending Value',
          sortable: true,
          editor: PriceEditor,
          body: (row) => formatCurrency(row.endingValue),
          style: { minWidth: '200px' },
          filterField: 'endingValue',
          ...DEFAULT_NUMBER_OPTIONS,
          filterElement: (options) => (
            <CustomTextFilter
              options={options}
              handleChange={(value) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  endingValue: {
                    value,
                    matchMode: prevFilters.endingValue.matchMode,
                  },
                }))
              }}
              type="Number"
            />
          ),
          onFilterMatchModeChange: (_field, mode) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              endingValue: {
                value: prevFilters.endingValue.value,
                matchMode: mode,
              },
            }))
          },
        },
        {
          field: 'grossReturn',
          header: 'Gross Return',
          sortable: true,
          editor: PercentageEditor,
          body: (row) =>
            row.grossReturn !== null ? `${(row.grossReturn * 100).toFixed(2)}%` : '-',
          style: { minWidth: '180px' },
          filterField: 'grossReturn',
          ...DEFAULT_NUMBER_OPTIONS,
          filterElement: (options) => (
            <CustomTextFilter
              options={options}
              handleChange={(value) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  grossReturn: {
                    value,
                    matchMode: prevFilters.grossReturn.matchMode,
                  },
                }))
              }}
              type="Number"
            />
          ),
          onFilterMatchModeChange: (_field, mode) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              grossReturn: {
                value: prevFilters.grossReturn.value,
                matchMode: mode,
              },
            }))
          },
        },
        {
          field: 'netReturn',
          header: 'Net Return',
          sortable: true,
          editor: PercentageEditor,
          body: (row) => (row.netReturn !== null ? `${(row.netReturn * 100).toFixed(2)}%` : '-'),
          style: { minWidth: '180px' },
          filterField: 'netReturn',
          ...DEFAULT_NUMBER_OPTIONS,
          filterElement: (options) => (
            <CustomTextFilter
              options={options}
              handleChange={(value) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  netReturn: {
                    value,
                    matchMode: prevFilters.netReturn.matchMode,
                  },
                }))
              }}
              type="Number"
            />
          ),
          onFilterMatchModeChange: (_field, mode) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              netReturn: {
                value: prevFilters.netReturn.value,
                matchMode: mode,
              },
            }))
          },
        },
        {
          field: 'isIncluded',
          header: 'Included',
          sortable: true,
          editor: StatusEditor,
          body: (row) => renderStatusTag(row.isIncluded),
          style: { minWidth: '160px' },
          filterField: 'isIncluded',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderRadioFilter(
              YES_OR_NO,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  isIncluded: {
                    value: YES_OR_NO.find((o) => o.label === selected)?.value,
                  },
                }))
              },
              'Filter by Included Status',
              options,
            ),
        },
        {
          field: 'isFeePaying',
          header: 'Fee Paying',
          sortable: true,
          editor: StatusEditor,
          body: (row) => renderStatusTag(row.isFeePaying),
          style: { minWidth: '160px' },
          filterField: 'isFeePaying',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderRadioFilter(
              YES_OR_NO,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  isFeePaying: {
                    value: YES_OR_NO.find((o) => o.label === selected)?.value,
                  },
                }))
              },
              'Filter by Fee Paying Status',
              options,
            ),
        },
        {
          field: 'hasBundledFee',
          header: 'Bundled Fee',
          sortable: true,
          editor: StatusEditor,
          body: (row) => renderStatusTag(row.hasBundledFee),
          style: { minWidth: '160px' },
          filterField: 'hasBundledFee',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderRadioFilter(
              YES_OR_NO,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  hasBundledFee: {
                    value: YES_OR_NO.find((o) => o.label === selected)?.value,
                  },
                }))
              },
              'Filter by Bundled Fee Status',
              options,
            ),
        },
        {
          field: 'comments',
          header: 'Comments',
          editor: TextAreaEditor,
          style: { minWidth: '300px' },
          filterField: 'comments',
          ...DEFAULT_FILTER_OPTIONS,
          filterElement: (options) => (
            <CustomTextFilter
              options={options}
              handleChange={(value) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  comments: {
                    value,
                    matchMode: prevFilters.comments.matchMode,
                  },
                }))
              }}
            />
          ),
          onFilterMatchModeChange: (_field, mode) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              comments: {
                value: prevFilters.comments.value,
                matchMode: mode,
              },
            }))
          },
        },
        {
          field: 'ruleViolations',
          header: 'Rule Violations',
          body: (row) => _.join(row.ruleViolations, ', '),
          style: { minWidth: '300px' },
          filterField: 'ruleViolations',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderRadioFilter(
              RADIO_OPTIONS,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  ruleViolations: {
                    value: RADIO_OPTIONS.find((o) => o.label === selected)?.value,
                  },
                }))
              },
              'Filter by Rule Violations',
              options,
            ),
        },
      ]

      let updatedColumns = DEFAULT_COLUMNS

      // Add columns for Portfolios to the second and third column if household data is available
      if (client?.hasAnyHouseholdData) {
        const HOUSEHOLD_COLUMNS = _.cloneDeep(DEFAULT_COLUMNS)
        HOUSEHOLD_COLUMNS.splice(1, 0, {
          field: 'portfolio.id',
          header: 'Portfolio',
          body: (row) => `${row.portfolio?.name} (${row.portfolio?.number})`,
          sortField: 'portfolio__name',
          sortable: true,
          style: { minWidth: '230px' },
          filterField: 'portfolio',
          ...CUSTOM_FILTER_OPTIONS,
          editor: (options) =>
            renderSelectEditor(
              updatedPortfolios,
              options.rowData.portfolio?.id,
              'Select a Portfolio',
              options,
            ),
          filterElement: (options) =>
            renderMultiSelectFilter(
              updatedPortfolios,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  portfolio: {
                    value: selected,
                  },
                }))
              },
              'Filter by Portfolio',
              options,
            ),
        })

        updatedColumns = HOUSEHOLD_COLUMNS
      }

      // Check for additional columns on the client and dynamically update the columns for display
      if (client?.portfolioDataAdditionalColumns) {
        const additionalColumns = client.portfolioDataAdditionalColumns.map((column) => ({
          field: `editableAdditionalData.${column}`,
          body: (row) => (row.additionalData ? row.additionalData[column] : ''),
          header: column,
          editor: TextEditor,
          style: { minWidth: '200px' },
        }))

        updatedColumns = [...updatedColumns, ...additionalColumns]
      }

      setAccounts(updatedAccounts)
      setComposites(updatedComposites)
      setPeriods(updatedPeriods)
      setPortfolios(updatedPortfolios)
      setColumns(updatedColumns)
    }

    getClientData()
  }, [clientId])

  /**
   * Gets the updated list of portfolio data; updates pagination.
   * @param {string} url
   */
  const getUpdatedPortfolioData = async (url) => {
    const response = await getPortfolioData(url, handleErrors, setLoadingPortfolioData)

    if (response) {
      setTotalRecords(response.count)
      setPortfolioData(
        _.map(response.results, (d) => ({
          ...d,
          editableAdditionalData: d.additionalData ? { ...d.additionalData } : null,
        })),
      )
    }
  }

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of data.
   */
  useEffect(() => {
    if (clientId) {
      let queryFilters = configureFilterQuery({
        account: filters.account || DEFAULT_FILTERS.account,
        composite: filters.composite || DEFAULT_FILTERS.composite,
        period: filters.period || DEFAULT_FILTERS.period,
        portfolio: filters.portfolio || DEFAULT_FILTERS.portfolio,
        beginning_value: filters.beginningValue || DEFAULT_FILTERS.beginningValue,
        ending_value: filters.endingValue || DEFAULT_FILTERS.endingValue,
        gross_return: filters.grossReturn || DEFAULT_FILTERS.grossReturn,
        net_return: filters.netReturn || DEFAULT_FILTERS.netReturn,
        comments: filters.comments || DEFAULT_FILTERS.comments,
      })
      // Manually handle rule violations since this is a boolean value
      if (filters.ruleViolations?.value !== null) {
        queryFilters = `${queryFilters}&rule_violations=${filters.ruleViolations.value}`
      }
      if (filters.isIncluded?.value !== null) {
        queryFilters = `${queryFilters}&is_included=${filters.isIncluded.value}`
      }
      if (filters.isFeePaying?.value !== null) {
        queryFilters = `${queryFilters}&is_fee_paying=${filters.isFeePaying.value}`
      }
      if (filters.hasBundledFee?.value !== null) {
        queryFilters = `${queryFilters}&has_bundled_fee=${filters.hasBundledFee.value}`
      }

      getUpdatedPortfolioData(
        `${PORTFOLIO_DATA_BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}&${queryFilters}`,
      )
    }
  }, [perPage, clientId, sortedColumn, currentPage, filters, refreshCounter])

  /**
   * Handles row edit completion by updating the local `portfolioData` with new data and
   * submitting it to the backend. Converts `period` to ISO string if it's a Date object.
   * Sends the updated row to `updatePortfolioData` and updates the UI on success.
   * @param {object} event
   */
  const onRowEditComplete = async ({ newData, index }) => {
    const oldPortfolioData = [...portfolioData]
    const updatedData = [...portfolioData]

    if (newData.period instanceof Date) {
      const [date] = newData.period.toISOString().split('T')
      // eslint-disable-next-line no-param-reassign
      newData.period = date
    }

    // Update the composite, account and portfolio (if applicable) to update the stored values.
    // Fall back to the row's existing value when the id isn't in the locally-cached dropdown
    // list (the lists are fetched once on mount and can lag the row data) — otherwise `find`
    // returns undefined, which the column body renderers dereference and crash the whole app.
    updatedData[index] = { ...newData, additionalData: newData.editableAdditionalData }
    updatedData[index].composite =
      composites.find((c) => c.id === newData.composite.id) ?? newData.composite
    updatedData[index].account =
      accounts.find((a) => a.id === newData.account.id) ?? newData.account
    if (newData.portfolio) {
      updatedData[index].portfolio =
        portfolios.find((p) => p.id === newData.portfolio.id) ?? newData.portfolio
    }

    // Optimistically update the data to prevent showing internal account values
    setPortfolioData(updatedData)

    await updatePortfolioData(
      clientId,
      newData.id,
      {
        account: newData.account.id,
        composite: newData.composite.id,
        beginningValue: newData.beginningValue,
        endingValue: newData.endingValue,
        comments: newData.comments,
        grossReturn: newData.grossReturn,
        hasBundledFee: newData.hasBundledFee,
        isFeePaying: newData.isFeePaying,
        isIncluded: newData.isIncluded,
        netReturn: newData.netReturn,
        period: newData.period,
        portfolio: newData.portfolio?.id || null,
        additionalData: newData.editableAdditionalData,
      },
      (m) => {
        // Reset back to previous value
        setPortfolioData(oldPortfolioData)
        handleErrors(m)
      },
      () => {},
      (m) => handleSuccess(m),
    )

    getUpdatedClient()
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loadingClient}>
        <div className="size-full">
          <ClientHeader client={client} />

          <div className="flex h-[calc(100vh-150px)] w-full flex-col bg-white px-4 pb-12 pt-6 sm:px-6 lg:px-8">
            <div className="flex size-full flex-col space-y-6">
              <div className="flex w-full flex-col justify-between sm:flex-row sm:items-center">
                <div className="flex flex-row items-center gap-2">
                  <h3 className="text-xl font-semibold leading-6 text-gray-900">Portfolio Data</h3>

                  {client?.lastImportAt !== null && (
                    <Tooltip
                      content={
                        <div className="w-[280px] rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5">
                          <span className="text-xs font-medium">
                            {isImportInProgress
                              ? 'Cannot edit while import is in progress.'
                              : 'Cannot edit while running CTV6.'}
                          </span>
                        </div>
                      }
                      display={!canModifyData}
                      placement="bottom-end"
                    >
                      <Toggle
                        label="Enable Editing"
                        disabled={!canModifyData}
                        onChange={() => {
                          if (dataEditable) {
                            // Hack to click on all cancel buttons for rows that are in edit mode
                            const cancelButtons = document.querySelectorAll(
                              "button[name='row-cancel']",
                            )
                            cancelButtons.forEach((button) => button.click())
                          }

                          setDataEditable(!dataEditable)
                        }}
                        checked={dataEditable}
                      />
                    </Tooltip>
                  )}
                </div>

                {client?.lastImportAt !== null && (
                  <div className="mt-3 flex flex-row gap-2 sm:ml-4 sm:mt-0">
                    {hasAnyFilterSelected(filters) && (
                      <Button
                        label="Clear Filters"
                        background="bg-gray"
                        onClick={() => {
                          // Clear search params if there are any
                          if (_.keys(Object.fromEntries(searchParams)).length > 0) {
                            navigate(`/clients/${clientId}/portfolio-data`)
                          }

                          setFilters(DEFAULT_FILTERS)
                        }}
                      />
                    )}

                    {dataEditable && (
                      <Button label="Add Row" onClick={() => setShowAddRowModal(true)} />
                    )}

                    <Button
                      background="bg-orange"
                      label="Export Data"
                      onClick={() => setShowExportDataModal(true)}
                    />

                    <CustomLink to={`/clients/${clientId}/imports/new`}>
                      {isImportInProgress ? 'View Import' : 'Import Data'}
                    </CustomLink>
                  </div>
                )}
              </div>

              {client?.lastImportAt === null ? (
                <NoDataPrompt
                  path={`/clients/${clientId}/imports/new`}
                  title="No Portfolio Data Available"
                  subtitle={`Please ${
                    client?.pendingDataImport ? 'finish creating' : 'create'
                  } portfolio data before generating a report.`}
                  linkText={client?.pendingDataImport ? 'View Pending Import' : 'Import Data'}
                />
              ) : (
                <DataTable
                  data={portfolioData}
                  columns={columns}
                  loading={loadingPortfolioData}
                  rowsPerPageOptions={[50, 100, 500, 1000]}
                  pagination={pagination}
                  sorting={sorting}
                  loadingDelete={loadingDelete}
                  onRowEditComplete={onRowEditComplete}
                  onRowDeleteConfirm={(row) => {
                    deletePortfolioData(clientId, row.id, handleErrors, setLoadingDelete, () => {
                      handleSuccess('Portfolio data row deleted.')
                      refreshPortfolioData()
                    })
                  }}
                  areRowsDeleteable={dataEditable}
                  areRowsEditable={dataEditable}
                  hoverEffect={false}
                  rowDeleteConfirm={(row) => ({
                    title: `Delete Portfolio Data Row`,
                    type: 'warning',
                    message: (
                      <div className="flex flex-col">
                        <span className="">Are you sure you want to delete the row for:</span>
                        <div className="mt-1.5 flex gap-1">
                          Composite:
                          <span className="font-semibold">{row.composite.name}</span>
                        </div>

                        <div className="flex gap-1">
                          Account:
                          <span className="font-semibold">
                            {row.account.name} ({row.account.number})
                          </span>
                        </div>

                        <div className="flex gap-1">
                          Period:
                          <span className="font-semibold">{row.period}</span>
                        </div>
                      </div>
                    ),
                  })}
                  filters={filters}
                  onFilter={(e) => setFilters(e.filters)}
                  ref={dataTable}
                />
              )}
            </div>
          </div>
        </div>
      </StateContainer>

      {showAddRowModal && (
        <AddPortfolioDataModal
          accounts={accounts}
          client={client}
          composites={composites}
          closeModal={() => setShowAddRowModal(false)}
          onSuccess={() => {
            setShowAddRowModal(false)
            refreshPortfolioData()
          }}
          periods={periods}
          portfolios={portfolios}
        />
      )}

      {showExportDataModal && (
        <ExportDataModal
          client={client}
          closeModal={() => setShowExportDataModal(false)}
          periods={periods}
        />
      )}
    </PageContainer>
  )
})

export default Portfolio
